// ** Optimized Icon Hook
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getCachedIconData } from "./IconCache";
import { getIcon, loadIcon } from "./LazyIconLoader";

// #####################################################

/**
 * Optimized icon hook z lazy loading i cache
 */

const useOptimizedIcon = (iconName, variant = "solid", options = {}) => {
	const {
		enableLazyLoading = false,
		fallbackIcon = null,
		preload = false,
		debounceMs = 0,
	} = options;

	const [iconData, setIconData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	const timeoutRef = useRef(null);
	const mountedRef = useRef(true);

	// Cleanup na unmount
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	// Debounced icon loading
	const loadIconData = useCallback(
		(name, var_) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(async () => {
				if (!mountedRef.current) return;

				try {
					setIsLoading(true);
					setError(null);

					let data;

					if (enableLazyLoading) {
						data = getIcon(name, var_);

						if (!data) {
							const fullIconData = await loadIcon(name);
							if (fullIconData && mountedRef.current) {
								data = {
									viewBox: fullIconData.viewBox,
									path:
										fullIconData.variants?.[var_]?.path ||
										fullIconData.path,
								};
							}
						}
					} else {
						data = getCachedIconData(name, var_);
					}

					if (mountedRef.current) {
						setIconData(data);
						setIsLoading(false);
					}
				} catch (err) {
					if (mountedRef.current) {
						setError(err);
						setIsLoading(false);

						// Fallback icon
						if (fallbackIcon) {
							try {
								const fallbackData = getCachedIconData(
									fallbackIcon,
									var_,
								);
								setIconData(fallbackData);
							} catch (fallbackErr) {
								console.warn(
									"Fallback icon also failed:",
									fallbackErr,
								);
							}
						}
					}
				}
			}, debounceMs);
		},
		[enableLazyLoading, fallbackIcon, debounceMs],
	);

	useEffect(() => {
		if (iconName) {
			loadIconData(iconName, variant);
		}
	}, [iconName, variant, loadIconData]);

	useEffect(() => {
		if (preload && iconName && enableLazyLoading) {
			loadIcon(iconName).catch(console.warn);
		}
	}, [preload, iconName, enableLazyLoading]);

	const result = useMemo(
		() => ({
			iconData,
			isLoading,
			error,
			isReady: !isLoading && !error && iconData,
		}),
		[iconData, isLoading, error],
	);

	return result;
};

// #####################################################

export default useOptimizedIcon;

// #####################################################
